


























































































import { Component, Watch, Ref, Prop } from 'vue-property-decorator'
import AudienceDetails from '@/components/TestDetails/AudienceDetails.vue'
import TestMetaData from '@/components/TestDetails/TestMetaData.vue'
import { Location } from '@/components/Templates/TemplateManager.vue'
import RelatedTests from '@/components/TestDetails/RelatedTests.vue'
import Reports from '@/components/TestDetails/Reports.vue'
import CompletedTest from '@/components/TestDetails/CompletedTest.vue'
import InProgressTest from '@/components/TestDetails/InProgressTest.vue'
import DraftOrSubmittedTest from '@/components/TestDetails/DraftOrSubmittedTest.vue'
import TestDetailsHeader from '@/components/TestDetails/TestDetailsHeader.vue'
import { AssetType, Card } from '@alpha/ink'
import Comments from '@/components/Comments/Comments.vue'
import Feedback from '@/components/TestDetails/Feedback.vue'
import store from '@/store'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import api from '@/api'
import analytics from '@/analytics'
import Toast from '@/components/Toast.vue'
import { isDraftOrSubmitted, livechartsEnabled } from '@/utilities/test-helpers'
import { User } from '@/types/frontend'
import { PresetTestMessages } from '@/store/modules/notification-store-module'
import { EditProjectPayload } from '@/components/TestDetails/EditProjects.vue'
import AdminRouteCheck from '@/mixins/AdminRouteCheck.vue'

import { mixins } from 'vue-class-component'

export interface FollowerData {
  user: User,
  follow: boolean
}

@Component({
  components: {
    AudienceDetails,
    TestMetaData,
    Comments,
    Feedback,
    RelatedTests,
    CompletedTest,
    InProgressTest,
    DraftOrSubmittedTest,
    Card,
    TestDetailsHeader,
    Reports,
    Toast
  }
})
export default class TestDetailsLegacy extends mixins(AdminRouteCheck) {
  @Prop({ required: true }) users!: User[]
  @Prop({ required: true }) followers!: User[]

  @Ref('draftOrSubmittedTest') readonly draftOrSubmittedTestComponent!: DraftOrSubmittedTest

  selectedTemplate: graph.Template | null = null
  tagsUpdating = false
  templateInputsValidated = false
  templatesValidationError:boolean = false
  location = Location.DraftOrSubmittedTest

  mounted () {
    if (this.projectId) {
      store.commit.testCreation.setProjectId(this.projectId)
    }
  }

  @Watch('test', { immediate: true })
  async updateTemplate () {
    if (this.isTemplated && this.test!.payload.testTemplateId) {
      if (!store.state.workspace.accountId) {
        console.error('Missing account id')
        return
      }
      const templateId = `${this.test!.payload.testTemplateId}`
      const selectedTemplate = await api.getTemplateById(templateId, this.accountId!)
      if (!selectedTemplate) {
        console.error('No template found', this.test!.payload.testTemplateId)
        return
      }
      this.selectedTemplate = selectedTemplate
      if (this.templatesValidationError) {
        this.validateTemplate()
      }
    }
  }

  get showTestResults () {
    if (this.hasVideoAssets) return true
    return this.test.payload.status === db.TestStatus.Complete || (this.test.payload.status === db.TestStatus.InProgress && this.isAdmin)
  }

  get hasAssets () {
    return this.test.payload.assets && !!Object.keys(this.test.payload.assets).length
  }

  get hasVideoAssets () {
    return this.hasAssets && Object.values(this.test.payload.assets).some(asset => asset.asset_type === AssetType.VIDEO)
  }

  get livechartsEnabled () {
    return livechartsEnabled(this.test, this.queryOverride)
  }

  get respondentCount () {
    if (this.livechartsEnabled) {
      return store.getters.survey.respondentCount
    } else {
      return this.test.payload.people
    }
  }

  get showInProgress () {
    return this.test.payload.status === db.TestStatus.InProgress
  }

  get workspaceId () {
    return this.$route.params.workspaceId
  }

  get projects () {
    return store.getters.workspace.getProjectsByTestId(this.testId)
  }

  get projectId () {
    return this.$route.params.projectId
  }

  get project () {
    if (this.projectId) {
      return store.getters.workspace.getProjectById(this.projectId)
    }

    return undefined
  }

  get testId () {
    return this.$route.params.testId
  }

  get accountId () {
    return store.state.workspace.accountId
  }

  get organizationId () {
    return store.state.workspace.organizationId
  }

  get test () {
    const mgtTest = store.getters.workspace.getMgtTestById(this.testId)
    return mgtTest?.data as db.Test
  }

  get audience () {
    return this.test.payload.segment ? store.getters.workspace.getAudienceById(this.test.payload.segment.id) : null
  }

  get assets () {
    return this.test.payload.prototypes
  }

  get isTemplatedAndEditable () {
    return !['Complete', 'In Progress'].includes(this.test.payload.status) && this.selectedTemplate !== null
  }

  async tagsUpdated (tags: string[]) {
    this.tagsUpdating = true

    const testPayload = { tags: tags.join(',') }
    await store.dispatch.workspace.updateTest({ test: testPayload, workspaceId: this.workspaceId, testId: this.testId })

    this.tagsUpdating = false
  }

  get canClone () {
    if (this.test.payload.testType !== 'Template') {
      return true
    }
    if (this.selectedTemplate?.hidden === true || !this.selectedTemplate) {
      return false
    } else {
      return true
    }
  }

  editFollower (event: FollowerData) {
    if (event.follow) {
      this.followers.push(event.user)
    } else {
      const followerIdx = this.followers.findIndex((follower) => {
        return follower.id === event.user.id
      })
      if (followerIdx >= 0) this.followers.splice(followerIdx, 1)
    }
    this.saveFollowers(event.follow, event.user.id)
  }

  // Both adding and removing followers go through here. May want to split this into multiple routes where one is adding yourself, while the other is adding others
  // Also there's a small potential for users to be emailed multiple times being added to a single test since it relies on redis sets in MGT and we're adding/removing each user individually instead of in a batch now
  saveFollowers (follow: boolean, uid: string) {
    const userIds = this.followers.map((u) => { return u.id })
    const testTitle = this.test?.payload.title ?? ''

    api.subscribeGroup(this.workspaceId, this.testId, userIds, testTitle)

    const eventName = follow ? 'Follow Test' : 'Unfollow Test'
    analytics.track(eventName, {
      testId: this.testId,
      workspaceId: this.workspaceId,
      uid: uid
    })
  }

  async associateProjects (data: EditProjectPayload) {
    const newIds = data.project.ids
    const updateData: graph.UpdateProjectInput = {}

    if (data.follow) {
      newIds.push(this.testId)
    } else {
      newIds.splice(newIds.findIndex((id: string) => id === this.testId), 1)
      updateData.delete = { ids: [this.testId] }
    }

    updateData.ids = newIds
    const newProject = await store.dispatch.workspace.updateProject({
      id: data.project.id,
      projectData: updateData
    })

    const failedInclusion = data.follow && !newProject?.ids.includes(this.testId)
    const failedRemoval = !data.follow && newProject?.ids.includes(this.testId)
    const successfulRemovalFromCurrentProject = !data.follow && this.project?.id === data.project.id

    if (failedInclusion || failedRemoval) {
      this.toast = 'Failed to update project associations.'
    } else if (successfulRemovalFromCurrentProject) {
      this.$router.push(`/workspaces/${this.workspaceId}/tests/${this.testId}`)
    }
  }

  get relatedTests () {
    const uniqueRelatedTests = this.projects.reduce((acc, project) => {
      project.ids.forEach(id => {
        acc[id] = true
      })
      return acc
    }, {} as Record<string, boolean>)

    if (this.test.payload.relatedIds) { // test is a clone of a previous test
      this.test.payload.relatedIds.forEach(id => {
        uniqueRelatedTests[id] = true
      })
    }

    const relatedTests: db.Test[] = Object.keys(uniqueRelatedTests).reduce((acc, id) => {
      const test = store.getters.workspace.tests.find(t => {
        return t.id === parseInt(id) && t.id !== this.test.id
      })
      if (test) acc.push(test)
      return acc
    }, [] as db.Test[])

    return relatedTests
  }

  get isDraftOrSubmitted () {
    return this.test && isDraftOrSubmitted(this.test.payload.status)
  }

  get isTemplated () {
    if (this.test) {
      return this.test.payload.testType === 'Template'
    } else {
      return false
    }
  }

  /**
   * This getter joins info from the MGT suplemental users API to the test drop.
   * We should get rid of this once we switch to GraphQL for all of our test
   * data. With GraphQL, we can just embed the `creator` inside the `Test`.
   */
  get testWithCreatorName (): db.Test | null {
    const { test } = this
    if (!test) {
      return null
    }

    const creator = this.users.filter(user => user.id === test.created_by.id)[0]
    if (creator) {
      test.created_by.name = creator.fullName
    }

    return test
  }

  async updateTest (payload: Partial<db.TestPayload>) {
    const test = await store.dispatch.workspace.updateTest({ test: payload, workspaceId: this.workspaceId, testId: this.test!.id.toString() })
    if (!test) {
      this.toast = PresetTestMessages.updateFail
    } else {
      this.toast = PresetTestMessages.updateSuccess
    }
  }

  set toast (value: string | false) {
    store.dispatch.notification.setMessage(value)
  }

  get toast () {
    return store.state.notification.message
  }

  get authenticatedUser () {
    return store.getters.user.authenticatedUser
  }

  get shouldShowFeedback () {
    if (!this.hasFeature('testFeedbackBox')) return false

    if (this.test.payload.status !== 'Complete') return false

    const userIds = this.test.payload.feedback

    if (userIds == null) return true

    const hasUserGivenFeedback = Object.keys(userIds).includes(this.authenticatedUser.uid)
    return !hasUserGivenFeedback
  }

  updateAudience (newAudience: db.AudienceReference) {
    this.updateTest({ segment: newAudience })
  }

  hasFeature (feature: string): boolean {
    return !!store.getters.feature.hasFeature(feature)
  }

  validateTemplate () {
    this.templateInputsValidated = this.draftOrSubmittedTestComponent.questionsTemplateManagerComponent.validate()
    this.templatesValidationError = !this.templateInputsValidated
  }
}

